import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_CONTAINER_COLUMN, H_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { Modal } from "bootstrap";
import Quill, { Range } from "quill";
import { i18n, I18nType } from "./i18n/i18n";

/**
 * RichTextEditor use Quill library
 */
@CustomElement({
    name: "cjs-quill-link-module",
    style: require("./quillLinkModule.scss"),
    template: require("./quillLinkModule.html"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN, H_100],
    i18n
})
export class CjsQuillLinkModule extends WebComponent<I18nType> {

    /* -- Components -- */
    @TypedHtmlElements()
    public readonly components!: {
        linkForm: HTMLFormElement,
        inputUrl: HTMLInputElement,
        inputText: HTMLInputElement,
        linkModal: HTMLElement,
        linkModalLabel: HTMLElement,
        inputTextLabel: HTMLElement,
        confirmButton: HTMLButtonElement,
        cancelButton: HTMLButtonElement
    };

    /** Modal */
    public modal?: Modal;

    /** Previous range selected */
    public savedRange: Range | null = null;

    constructor(public quillEditor: Quill, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        document.body.append(this);
    }

    /**
     * When component is loaded
     */
    public override init(): void {
        super.init();

        this.components.linkModalLabel.innerHTML = this.i18n.linkModalLabel;
        this.components.inputTextLabel.innerHTML = this.i18n.inputTextLabel;
        this.components.inputText.placeholder = this.i18n.inputText.placeholder;
        this.components.confirmButton.innerHTML = this.i18n.confirmButton;
        this.components.cancelButton.innerHTML = this.i18n.cancelButton;

        this.modal = new Modal(this.components.linkModal);
    }


    /** Open Bootstrap modal with pre-filled values */
    public openCustomLinkModal(_value?: unknown): void {
        this.modal?.show();

        this.components.linkForm.addEventListener("submit", (e) => {
            e.preventDefault();

            const url = this.normalizeUrl(this.components.inputUrl.value);
            const displayText = this.components.inputText.value.trim();

            if (!url) {
                this.components.inputUrl.focus();
                return;
            }

            const currentRange = this.quillEditor.getSelection();
            const range = this.savedRange ?? currentRange;

            if (!range) {
                const len = this.quillEditor.getLength();
                this.insertLinkedText(len, url, displayText);
                this.closeLinkModal();
                return;
            }

            if (range.length > 0) {
                if (displayText) {
                    this.quillEditor.deleteText(range.index, range.length, Quill.sources.USER);
                    this.quillEditor.insertText(range.index, displayText, { link: url }, Quill.sources.USER);
                    this.quillEditor.setSelection(range.index + displayText.length, 0, Quill.sources.USER);
                } else {
                    this.quillEditor.formatText(range.index, range.length, "link", url, Quill.sources.USER);
                    this.quillEditor.setSelection(range.index + range.length, 0, Quill.sources.USER);
                }
                this.closeLinkModal();
                return;
            }

            this.insertLinkedText(range.index, url, displayText);
            this.closeLinkModal();
        });

        this.savedRange = this.quillEditor.getSelection();
        const fmt: Record<string, anyReallyAny> = this.savedRange ? this.quillEditor.getFormat(this.savedRange) : {};

        const link = fmt["link"] ?? "";
        this.components.inputUrl.value = link;

        const text = this.getSelectedTextOrEmpty(this.savedRange);
        this.components.inputText.value = text !== link ? text : "";

        setTimeout(() => this.components.inputUrl.focus(), 200); // wait until modal transition ends
    }

    /** Close modal */
    public closeLinkModal(): void {
        this.modal?.hide();
        this.components.linkForm.reset();
        this.quillEditor.focus();
    }

    /** Normalize given url */
    public normalizeUrl(raw: string): string {
        if (!raw) { return ""; }
        const trimmed = raw.trim();
        if (/^https?:\/\//i.test(trimmed)) { return trimmed; }
        if (/^[a-z+.-]+:/i.test(trimmed)) { return trimmed; }
        return "https://" + trimmed;
    }

    /** Get current selected text */
    public getSelectedTextOrEmpty(range: Range | null): string {
        if (!range || range.length === 0) { return ""; }
        return this.quillEditor.getText(range.index, range.length).replace(/\n$/, "");
    }

    /** Insert text in editor */
    public insertLinkedText(index: number, url: string, displayText?: string): void {
        const text = displayText && displayText.length > 0 ? displayText : url;
        this.quillEditor.insertText(index, text, { link: url }, Quill.sources.USER);
        this.quillEditor.setSelection(index + text.length, 0, Quill.sources.USER);
    }

}